import { parse, isBefore, subYears, isAfter } from 'date-fns'

/**
 * Regex pattern for validating first and last names
 * Allows letters (A-Z, a-z), periods (.), apostrophes ('), spaces, and hyphens (-)
 * Length: 2-30 characters
 */
export const NAME_PATTERN = /^[A-Za-z.' -]{2,30}$/

/**
 * Creates a react-hook-form pattern validation rule for name fields
 * @param errorMessage - The error message to display when validation fails
 * @returns A pattern validation rule object for react-hook-form
 * @example
 * rules={{
 *   required: t('common.required_field'),
 *   ...getNameValidationRule(t('form.first_name_error'))
 * }}
 */
export const getNameValidationRule = (errorMessage: string): { pattern: { value: RegExp; message: string } } => ({
  pattern: {
    value: NAME_PATTERN,
    message: errorMessage,
  },
})

const dobIsValid = (dateOfBirth: string): boolean => {
  // Parse the dateOfBirth string to a Date object
  const dob = parse(dateOfBirth, 'MM/dd/yyyy', new Date())

  const MIN_USER_AGE = 18
  const MAX_USER_AGE = 100

  // Calculate the date 18 years ago from the current date
  const minAgeYearsAgo = subYears(new Date(), MIN_USER_AGE)
  const maxAgeYearsAgo = subYears(new Date(), MAX_USER_AGE)

  // Check if the date of birth is before 18 years ago
  return isBefore(dob, minAgeYearsAgo) && isAfter(dob, maxAgeYearsAgo)
}

// Helper function to validate that a date is in the past
const isDateInPast = (dateString: string): boolean => {
  // If empty, let the required rule handle it
  if (!dateString) {
    return true
  }

  // If incomplete (less than 10 characters), return true to avoid premature validation
  if (dateString.length < 10) {
    return true
  }

  // If exactly 10 characters, validate the date
  if (dateString.length === 10) {
    try {
      // Parse the date string (MM/DD/YYYY format)
      const inputDate = parse(dateString, 'MM/dd/yyyy', new Date())
      const currentDate = new Date()

      // Check if the parsed date is valid
      if (isNaN(inputDate.getTime())) {
        return false
      }

      // Check if the date is in the past
      return isBefore(inputDate, currentDate)
    } catch {
      return false
    }
  }

  // For any other length, it's invalid
  return false
}

/**
 * Converts a date from MM/dd/yyyy format to RFC3339 YYYY-MM-DD format
 * @param dateString - Date string in MM/dd/yyyy format (e.g., "6/30/2020" or "06/30/2020")
 * @returns Date string in YYYY-MM-DD format (e.g., "2020-06-30")
 * @example
 * formatDateForAPI("6/30/2020") // "2020-06-30"
 * formatDateForAPI("06/30/2020") // "2020-06-30"
 * formatDateForAPI("6/30/20") // "2020-06-30" (fixes 2-digit year)
 */
const formatDateForAPI = (dateString: string): string => {
  const [month, day, year] = dateString.split('/')

  // Fix incomplete years by assuming 20xx for 2-digit years
  let fullYear = year
  if (year && year.length === 2) {
    fullYear = `20${year}`
  } else if (!year || year.length < 2) {
    // If year is missing or too short, default to current year
    fullYear = new Date().getFullYear().toString()
  }

  return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export { dobIsValid, isDateInPast, formatDateForAPI }
